import { useMemo } from 'react';
import { isDevelopment } from './useEnvironment';

/**
 * Configuration values for development environment.
 */
const DEV_CONFIG = {
  GOOGLE_ANALYTICS_MEASUREMENT_ID: 'G-XZK0XM27HY',
  GOOGLE_ANALYTICS_UNIVERSAL_TRACKING_ID: 'UA-181562170-1',
  WOOTRIC_ACCOUNT_TOKEN: 'NPS-0041c451',
  WOOTRIC_DEBUG_MODE_ENABLED: 'true',
  ELMAHIO_API_KEY: 'b9f2d6924b97430e86e51c7a22d01120',
  ELMAHIO_LOG_ID: 'f31ff69e-1be5-4db5-966a-61d9f62eae3b',
  PREMIUM_SUBSCRIPTION_PRODUCT_ID: 'prod_ND4zUeFtPYA80V',
  PREMIUM_SUBSCRIPTION_PAYMENT_LINK: 'https://buy.stripe.com/test_eVag2Xemu3s50XmeUW',
  PREMIUM_SUBSCRIPTION_PAYMENT_LINK_MONTHLY: '',
  PREMIUM_SUBSCRIPTION_PAYMENT_LINK_ANNUAL: '',
  PREMIUM_SUBSCRIPTION_PROMOTION_CODE: 'INTRO50',
  INTRODUCTORY_DISCOUNT_PERCENT: 50,
  INTRODUCTORY_DISCOUNT_MONTHS: 3,
  IS_RETRO_ENABLED: true,
} as const;

/**
 * Configuration values for production environment.
 */
const PROD_CONFIG = {
  GOOGLE_ANALYTICS_MEASUREMENT_ID: 'G-BCW33YM97G',
  GOOGLE_ANALYTICS_UNIVERSAL_TRACKING_ID: 'UA-181562170-2',
  WOOTRIC_ACCOUNT_TOKEN: 'NPS-0041c451',
  WOOTRIC_DEBUG_MODE_ENABLED: 'false',
  ELMAHIO_API_KEY: 'b9f2d6924b97430e86e51c7a22d01120',
  ELMAHIO_LOG_ID: '120d7c83-67db-44d2-bace-f9c41a0a711a',
  PREMIUM_SUBSCRIPTION_PRODUCT_ID: 'prod_NVRtOTsWRwJXsL',
  PREMIUM_SUBSCRIPTION_PAYMENT_LINK: 'https://buy.stripe.com/8wM9CE9IS37T0BGeUU',
  PREMIUM_SUBSCRIPTION_PAYMENT_LINK_MONTHLY: '',
  PREMIUM_SUBSCRIPTION_PAYMENT_LINK_ANNUAL: '',
  PREMIUM_SUBSCRIPTION_PROMOTION_CODE: 'INTRO50',
  INTRODUCTORY_DISCOUNT_PERCENT: 50,
  INTRODUCTORY_DISCOUNT_MONTHS: 3,
  IS_RETRO_ENABLED: true,
} as const;

/**
 * React configuration interface.
 * Values are selected at runtime based on environment detection (DEV/PROD).
 * This allows a single frontend build artifact to be deployed to both development and production.
 */
export interface ReactConfig {
  GOOGLE_ANALYTICS_MEASUREMENT_ID: string;
  GOOGLE_ANALYTICS_UNIVERSAL_TRACKING_ID: string;
  WOOTRIC_ACCOUNT_TOKEN: string;
  WOOTRIC_DEBUG_MODE_ENABLED: string;
  ELMAHIO_API_KEY: string;
  ELMAHIO_LOG_ID: string;
  PREMIUM_SUBSCRIPTION_PRODUCT_ID: string;
  PREMIUM_SUBSCRIPTION_PAYMENT_LINK: string;
  PREMIUM_SUBSCRIPTION_PAYMENT_LINK_MONTHLY: string;
  PREMIUM_SUBSCRIPTION_PAYMENT_LINK_ANNUAL: string;
  PREMIUM_SUBSCRIPTION_PROMOTION_CODE: string;
  /** Percentage discount applied to the first INTRODUCTORY_DISCOUNT_MONTHS of a new subscription. Used for UI display only — the actual discount is applied via StripeCouponId on the backend. */
  INTRODUCTORY_DISCOUNT_PERCENT: number;
  /** Number of months the introductory discount applies. */
  INTRODUCTORY_DISCOUNT_MONTHS: number;
  /** Feature flag: when false, the Retrospective toggle and route are hidden. Set to false and redeploy to remove the UI after disabling IsRetroEnabled on the backend. */
  IS_RETRO_ENABLED: boolean;
}

/**
 * Environment-specific config without a React hook, so bootstrap code
 * (elmah.io) can run before the first render.
 */
export function getConfig(): ReactConfig {
  return isDevelopment() ? DEV_CONFIG : PROD_CONFIG;
}

/**
 * Hook to access React configuration with environment-specific values.
 * Selects values based on current environment (DEV/PROD).
 * 
 * @returns React configuration object with values for current environment
 */
export function useConfig(): ReactConfig {
  return useMemo(() => getConfig(), []);
}

